import Keycloak from 'keycloak-js';
import React, { useEffect, useMemo } from 'react';
import { KeycloakContext, KeycloakService } from './KeycloakContext';

export type KeycloakProviderProps = {
    authClient: Keycloak;
    initOptions: Keycloak.KeycloakInitOptions;
    children: React.ReactNode;
};

export const KeycloakProvider: React.FunctionComponent<KeycloakProviderProps> = function ({
    authClient,
    initOptions,
    children,
}) {
    const [keycloakService, setKeycloakService] = React.useState<KeycloakService | undefined>(
        undefined,
    );
    const [keycloakInstance, setKeycloakInstance] = React.useState<Keycloak>(authClient);
    const [initialized, setInitialized] = React.useState<boolean>(
        authClient.authenticated || false,
    );

    useEffect(() => {
        if (!initialized) {
            // throw new Error('KeycloakProvider: keycloak instance not initialized');
            keycloakInstance
                .init(initOptions)
                .then((authenticated) => {
                    setInitialized(authenticated);
                })
                .catch((error) => {
                    console.error('Keycloak init failed:', error);
                    setInitialized(false);
                });
        }
    }, [initOptions, initialized, keycloakInstance]);

    useEffect(() => {
        setInitialized(keycloakInstance.authenticated || false);
    }, [keycloakInstance.authenticated]);

    const contextValue = useMemo(
        () => ({
            keycloakInstance,
            setKeycloakInstance,
            initialized,
            setInitialized,
            keycloakService,
            setKeycloakService,
        }),
        [initialized, keycloakInstance, keycloakService],
    );

    React.useEffect(() => {
        setKeycloakService(new KeycloakService(keycloakInstance, initialized));
    }, [keycloakInstance, initialized]);

    return <KeycloakContext.Provider value={contextValue}>{children}</KeycloakContext.Provider>;
};
